import { useContext, useEffect, useMemo, useState } from 'react'
import { View, StyleSheet } from 'react-native'
import { AppNavigatorParams } from '../../../navigation/types'
import { StackScreenProps } from '@react-navigation/stack'
import { ScreenWrapper } from '../../../@ui/containers/grid/screen.wrapper'
import { useTranslation } from 'react-i18next'
import { useGlobalStyles } from '../../../contexts/global-styles.provider'
import { AuthContext, CurrentUser } from '../../../contexts/auth-context'
import {
  User,
  useAvailableNodesQuery,
  useUserQuery,
} from '../../../generated/graphql'
import { UserContributions } from './components/user-contributions.section'
import { Button, Switch, Text } from 'react-native-paper'
import { AppTheme, useTheme } from '../../../hooks/theme.hook'
import { SocialAccounts } from './components/social-accounts.section'
import Profile from './components/profile.detail'
import { UserTimeLineEvents } from './components/user-events'
import { CorankTrackerCard } from './components/corank-tracker.card'
import { useProfileCorankTrackRunners } from './hooks/profile-corank-track-runners.hook'
import NodeSelector from '../../core/clusters/shared/node.selector'
import type { MdNode } from '../../core/clusters/nodes-list/types'
import { useIsMobileDevice } from '../../../hooks/is-mobile.hook'

export function ProfileScreen({
  route,
  navigation,
}: StackScreenProps<AppNavigatorParams, 'Profile'>) {
  const { userId } = route.params

  const { t } = useTranslation('profile')
  const { userId: myUserId, username: myUsername } = useContext(AuthContext)
  const theme = useTheme()
  const styles = useStyle(theme)
  const { data } = useUserQuery({
    variables: {
      userId: userId || myUserId,
    },
  })
  const globalStyles = useGlobalStyles()

  const isProfileOwner = userId === myUserId
  const user = useMemo(() => data?.user, [data])
  const showCorankComparison = !!myUserId && !!userId && userId !== myUserId
  const isMobileLayout = useIsMobileDevice()
  const [followHighlightedRunner, setFollowHighlightedRunner] =
    useState(isMobileLayout)
  const [corankRaceNode, setCorankRaceNode] = useState<MdNode | undefined>(
    undefined
  )
  useEffect(() => {
    setCorankRaceNode(undefined)
  }, [userId])
  const { data: availableNodesData, loading: availableNodesLoading } =
    useAvailableNodesQuery({
      skip: !showCorankComparison,
      fetchPolicy: 'cache-and-network',
    })
  /** Public nodes share the all-public co-rank row; only private nodes need a distinct scope. */
  const privateCorankRaceNodes = useMemo(
    () =>
      (availableNodesData?.availableNodes ?? []).filter(
        (node) => node.isPrivate
      ),
    [availableNodesData?.availableNodes]
  )
  useEffect(() => {
    if (!corankRaceNode) {
      return
    }
    const all = availableNodesData?.availableNodes
    if (!all) {
      return
    }
    const match = all.find((n) => n.id === corankRaceNode.id)
    if (!match || !match.isPrivate) {
      setCorankRaceNode(undefined)
    }
  }, [corankRaceNode, availableNodesData?.availableNodes])

  const corankTrackRunners = useProfileCorankTrackRunners({
    profile: user,
    nodeId: corankRaceNode?.id,
  })
  const isExistingAccount = user?.firstName || user?.lastName
  return (
    <ScreenWrapper
      contentContainerStyle={{
        ...globalStyles.pageContainer,
        padding: theme.spacing(4),
      }}
    >
      {isExistingAccount ? (
        <Profile
          user={user as CurrentUser}
          theme={theme}
          isProfileOwner={isProfileOwner}
        />
      ) : (
        <Text variant={'titleLarge'}>{user?.username}</Text>
      )}
      <View style={{ marginVertical: theme.spacing(2) }} />

      {showCorankComparison ? (
        <View style={{ marginBottom: theme.spacing(3) }}>
          <View
            style={{
              marginBottom: theme.spacing(2),
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              columnGap: theme.spacing(2),
            }}
          >
            <View style={{ flex: 1, minWidth: 0, maxWidth: 320 }}>
              <NodeSelector
                loading={availableNodesLoading}
                selected={corankRaceNode}
                nodes={privateCorankRaceNodes}
                onSelect={(node) => setCorankRaceNode(node)}
                emptySelectionLabel={t('corank-race-public-co-rank')}
                publicOption={{
                  title: t('corank-race-public-co-rank'),
                  onSelect: () => setCorankRaceNode(undefined),
                }}
              />
            </View>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                flexShrink: 0,
                columnGap: theme.spacing(1),
              }}
            >
              <Text
                variant="labelLarge"
                numberOfLines={2}
                style={{ maxWidth: 120, textAlign: 'right' }}
              >
                {t('corank-race-follow-runner')}
              </Text>
              <Switch
                value={followHighlightedRunner}
                onValueChange={setFollowHighlightedRunner}
                accessibilityLabel={t('corank-race-follow-runner-a11y')}
              />
            </View>
          </View>
          <CorankTrackerCard
            key={corankRaceNode?.id ?? 'corank-race-public'}
            runners={corankTrackRunners}
            showEmptyTrack
            followHighlightedRunner={followHighlightedRunner}
            onRunnerProfilePress={({ userId }) => {
              navigation.navigate('Profile', { userId })
            }}
          />
        </View>
      ) : null}

      {isProfileOwner && (
        <>
          <SocialAccounts user={user as User} />
          <View style={{ marginVertical: theme.spacing(2) }} />
          <Button
            mode="outlined"
            icon="wallet"
            onPress={() => navigation.navigate('PaymentMethods')}
            style={{ marginVertical: theme.spacing(1) }}
          >
            {t('payment-methods')}
          </Button>
          <View style={styles.row}>
            {/* Space for other badges ... */}
            <View style={{ flex: 1 }}></View>

            <View style={{ flex: 1 }}></View>
          </View>
        </>
      )}

      <UserContributions
        style={{ marginVertical: theme.spacing(5) }}
        userId={userId}
      />
      {/* <UserPerspectives
        style={{ marginVertical: theme.spacing(5) }}
        userId={userId}
      /> */}

      {userId ?? myUserId ? (
        <>
          <Text variant={'titleMedium'}>{t('timeline')}</Text>
          <View style={{ marginVertical: theme.spacing(5) }}>
            <UserTimeLineEvents userId={userId ?? myUserId} />
          </View>
        </>
      ) : null}
    </ScreenWrapper>
  )
}

const useStyle = (theme: AppTheme) =>
  useMemo(
    () =>
      StyleSheet.create({
        row: {
          flexDirection: 'row',
          columnGap: theme.spacing(2),
          alignItems: 'center',
          marginBottom: theme.spacing(2),
        },
      }),
    [theme]
  )
export default ProfileScreen
